/**
 * ConfigContext — Backend-driven configuration.
 * Fetches GET /api/defaults on mount. Blocks rendering until loaded.
 * Merges country/currency catalogs with frontend fallbacks so a stale backend
 * deploy cannot hide ISO currencies (e.g. XOF) or countries (e.g. Senegal).
 */
import { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE_URL } from '@/config/api';
import { FALLBACK_COUNTRY_PRESETS, FALLBACK_CURRENCIES } from '@/utils/countryPresets';

const ConfigContext = createContext(null);

function mergeCountryPresets(backend) {
  return { ...FALLBACK_COUNTRY_PRESETS, ...(backend || {}) };
}

function mergeOperatingCurrencies(backend) {
  return [...new Set([
    ...FALLBACK_CURRENCIES,
    ...((Array.isArray(backend) && backend.length) ? backend : []),
  ])].sort();
}

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/defaults`)
      .then(r => r.json())
      .then(data => {
        if (!cancelled) {
          setConfig({
            assumptions: data.assumptions,
            sampleInputs: data.sampleInputs,
            formulas: data.formulas,
            sourceLabels: data.sourceLabels,
            countryPresets: mergeCountryPresets(data.countryPresets),
            operatingCurrencies: mergeOperatingCurrencies(data.operatingCurrencies),
          });
        }
      })
      .catch(err => {
        console.error('Failed to load backend defaults:', err);
        if (!cancelled) {
          setConfig({
            assumptions: null,
            sampleInputs: null,
            formulas: {},
            sourceLabels: {},
            countryPresets: FALLBACK_COUNTRY_PRESETS,
            operatingCurrencies: FALLBACK_CURRENCIES,
          });
        }
      });
    return () => { cancelled = true; };
  }, []);

  if (!config) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f3f4f6' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'system-ui' }}>Loading configuration...</div>
        </div>
      </div>
    );
  }

  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}

export function useConfig() {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error('useConfig must be used within ConfigProvider');
  return ctx;
}
